import { FileText } from '@crehana/compass.icons.outline/v2/file-text';
import { Button } from '@crehana/compass.ui/v3/button';
import { createColumns, DataTable } from '@crehana/compass.ui/v3/table';
import { useState } from 'react';
import { useTranslation } from '../i18n';
import type { TCollaboratorRow, TDailyDetail } from '../types';
import { DayRecordsDialog } from './day-records-dialog';

function formatDetailDate(isoDate: string): string {
  const date = new Date(`${isoDate}T12:00:00`);

  return date.toLocaleDateString('es', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

type TDailyBreakdownTableProps = {
  collaborator: TCollaboratorRow;
  days: TDailyDetail[];
};

export const DailyBreakdownTable = ({
  collaborator,
  days,
}: TDailyBreakdownTableProps) => {
  const { t } = useTranslation();
  const [selectedDay, setSelectedDay] = useState<TDailyDetail>();

  const columns = createColumns<TDailyDetail>(({ column, cells }) => [
    column.accessor('date', {
      header: t('DETAIL_COL_DATE'),
      cell: cells.text(row => ({
        title: formatDetailDate(row.date),
        bold: true,
      })),
    }),
    column.accessor('plannedHoursFormatted', {
      header: t('DETAIL_COL_PLANNED_HOURS'),
      cell: cells.text(row => ({
        title: row.plannedHoursFormatted,
        bold: true,
      })),
    }),
    column.accessor('workedHoursFormatted', {
      header: t('DETAIL_COL_WORKED_HOURS'),
      cell: cells.text(row => ({
        title: row.workedHoursFormatted,
        bold: true,
      })),
    }),
    column.accessor('overtimeFormatted', {
      header: t('DETAIL_COL_OVERTIME'),
      cell: info => {
        const value = info.row.original.overtimeFormatted;
        const isZero = value === '0:00 h';
        const isPositive = value.startsWith('+');

        return (
          <span
            className={`
              tw-cds-font-subtitle-01
              ${
                isZero
                  ? 'tw-cds-text-subtle'
                  : isPositive
                    ? 'tw-cds-text-success'
                    : 'tw-cds-text-alert'
              }`}
          >
            {value}
          </span>
        );
      },
    }),
    column.accessor('lateHoursFormatted', {
      header: t('DETAIL_COL_LATE_HOURS'),
      cell: info => {
        const value = info.row.original.lateHoursFormatted;
        const isZero = value === '0:00 h';
        const isPositive = value.startsWith('+');

        return (
          <span
            className={`
              tw-cds-font-subtitle-01
              ${
                isZero
                  ? 'tw-cds-text-subtle'
                  : isPositive
                    ? 'tw-cds-text-success'
                    : 'tw-cds-text-alert'
              }`}
          >
            {value}
          </span>
        );
      },
    }),
    column.display({
      id: 'actions',
      cell: info => (
        <Button
          type="button"
          size="xs"
          startIcon={FileText}
          onClick={() => setSelectedDay(info.row.original)}
        >
          {t('DETAIL_ACTION_REVIEW')}
        </Button>
      ),
    }),
  ]);

  return (
    <div className="tw-py-1.6 tw-px-2.4">
      <p className="tw-cds-font-label-01 tw-cds-text-subtle tw-uppercase tw-mb-1.2">
        {t('DAILY_BREAKDOWN_TITLE')}
      </p>
      <DataTable
        columns={columns}
        data={days}
        rowCount={days.length}
        density="compact"
      />

      {selectedDay && (
        <DayRecordsDialog
          open={!!selectedDay}
          onOpenChange={open => {
            if (!open) {
              setSelectedDay(undefined);
            }
          }}
          collaborator={collaborator}
          day={selectedDay}
        />
      )}
    </div>
  );
};
